import type { PostDTO } from '@bb/shared';
import { Button } from '@/components/ui/Button';
import { EmptyState, ErrorState, SkeletonRow, Sweep } from '@/components/ui/States';
import { PostCard } from './PostCard';

/**
 * Three across on a wide screen, folding down on narrower ones.
 *
 * `items-start` lets each card keep its natural height instead of stretching to
 * match the tallest in its row — a post with an image next to a one-liner would
 * otherwise leave a large void under the short one.
 */
const GRID = 'grid items-start gap-3 p-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-3';

export type PostListProps = {
  items: PostDTO[];
  liveIds?: Set<string>;
  isLoading: boolean;
  error: unknown;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  onRetry: () => void;
  onTogglePin: (post: PostDTO) => void;
  onToggleRead: (post: PostDTO) => void;
  emptyTitle: string;
  emptyDetail?: string;
  emptyAction?: React.ReactNode;
};

export function PostList({
  items,
  liveIds,
  isLoading,
  error,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  onRetry,
  onTogglePin,
  onToggleRead,
  emptyTitle,
  emptyDetail,
  emptyAction,
}: PostListProps) {
  if (isLoading) {
    return (
      <div className={GRID}>
        {Array.from({ length: 6 }, (_, index) => (
          <div
            key={index}
            className="flex gap-3 rounded-sharp border border-line bg-panel/60 p-3"
          >
            <div className="size-8 shrink-0 animate-pulse rounded-sharp bg-raised" />
            <div className="flex-1 space-y-2">
              <SkeletonRow className="w-32" />
              <SkeletonRow className="w-full" />
              <SkeletonRow className="w-3/4" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : 'Could not load posts.'}
        action={
          <Button size="sm" onClick={onRetry}>
            Retry
          </Button>
        }
      />
    );
  }

  if (items.length === 0) {
    return <EmptyState title={emptyTitle} detail={emptyDetail} action={emptyAction} />;
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className={GRID}>
        {items.map((post) => (
          <PostCard
            key={post.id}
            post={post}
            isNew={liveIds?.has(post.id) ?? false}
            onTogglePin={onTogglePin}
            onToggleRead={onToggleRead}
          />
        ))}
      </div>

      <div className="px-3 pb-4 pt-2">
        {isFetchingNextPage ? (
          <Sweep />
        ) : hasNextPage ? (
          <Button size="sm" className="w-full" onClick={onLoadMore}>
            Load older
          </Button>
        ) : (
          <p className="label-xs text-center">End of record</p>
        )}
      </div>
    </div>
  );
}
